/** Сортировка таблиц (`DataTable`) без React: сравнение текста с учётом языка, пустые значения в конец. */

export interface SortState {
  key: string;
  dir: "asc" | "desc";
}

export interface Sortable<T> {
  key: string;
  sort?: (a: T, b: T) => number;
}

/** Сравнение строк по правилам языка интерфейса (č, ć, š, ž на своих местах), регистр не важен, числа по значению. */
export function textComparator(locale: string): (a: string | null | undefined, b: string | null | undefined) => number {
  const collator = new Intl.Collator(locale, { sensitivity: "base", numeric: true });
  // Пустое значение всегда в конце, при любом направлении сортировки это делает `sortRows`.
  return (a, b) => collator.compare(a ?? "", b ?? "");
}

/** Пустые значения (null, "") в конец независимо от направления; остальное через `cmp`. */
export function emptyLast<T>(value: (row: T) => string | null | undefined, cmp: (a: string, b: string) => number, dir: "asc" | "desc") {
  return (a: T, b: T): number => {
    const x = value(a) ?? "";
    const y = value(b) ?? "";
    if (!x && !y) return 0;
    if (!x) return 1;
    if (!y) return -1;
    return dir === "asc" ? cmp(x, y) : cmp(y, x);
  };
}

/**
 * Строки в порядке сортировки: столбец без `sort` оставляет порядок как есть. Сравнение столбца получает
 * направление, чтобы «пустые в конец» работало и по убыванию (см. `emptyLast`); обычное сравнение просто переворачивается.
 */
export function sortRows<T>(
  rows: T[],
  columns: Array<Sortable<T> & { sortWithDir?: (dir: "asc" | "desc") => (a: T, b: T) => number }>,
  sort: SortState | null,
): T[] {
  if (!sort) return rows;
  const col = columns.find((c) => c.key === sort.key);
  if (!col) return rows;
  if (col.sortWithDir) return [...rows].sort(col.sortWithDir(sort.dir));
  if (!col.sort) return rows;
  const cmp = col.sort;
  return [...rows].sort((a, b) => (sort.dir === "asc" ? 1 : -1) * cmp(a, b));
}
